import { useContext, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthContext } from '../context/AuthContext'
import { subscriptionService, DEFAULT_PRODUCT } from '../services/subscriptionService'

export function useSubscription() {
  const [now, setNow] = useState(Date.now)
  const auth = useContext(AuthContext)
  const tenantId = auth?.user?.tenant_id ?? auth?.user?.tenantId
  const missingTenant = Boolean(auth?.isAuthenticated && !auth.isLoading && !tenantId)
  const client = useQueryClient()
  const query = useQuery({ queryKey: ['subscription', tenantId, DEFAULT_PRODUCT],
    queryFn: () => subscriptionService.subscription(), enabled: Boolean(auth?.isAuthenticated && tenantId),
    staleTime: 10_000, refetchInterval: 30_000, retry: false })
  useEffect(() => {
    const refresh = () => { client.invalidateQueries({ queryKey: ['subscription'] }); client.invalidateQueries({ queryKey: ['entitlements'] }) }
    window.addEventListener('saas-subscription-changed', refresh)
    return () => window.removeEventListener('saas-subscription-changed', refresh)
  }, [client])
  const original = query.data?.subscription
  const prepaid = original?.prepaidPeriodEnd && Date.parse(original.prepaidPeriodEnd) > Date.parse(original.periodEnd)
  const end = Math.min(Date.parse(prepaid ? original!.prepaidPeriodEnd! : original?.periodEnd ?? ''),
    original?.status === 'TRIALING' && original.trialEnd ? Date.parse(original.trialEnd) : Infinity)
  const effectiveNow = Math.max(now, Date.now())
  const expired = !!original && ['ACTIVE', 'TRIALING'].includes(original.status) && effectiveNow >= end
  const retentionDate = expired ? new Date(end + 90 * 86400000).toISOString() : original?.retention?.dataRetentionUntil
  const retentionOver = !!retentionDate && effectiveNow >= Date.parse(retentionDate)
  const periodAdjusted = original?.status === 'TRIALING' && Number.isFinite(end) ? { ...original, periodEnd: new Date(end).toISOString() } : original
  const subscription = expired && periodAdjusted ? { ...periodAdjusted, status: periodAdjusted.cancelAtPeriodEnd ? 'CANCELED' as const : 'EXPIRED' as const,
    retention: { state: retentionOver ? 'DATA_DELETION_ELIGIBLE' : 'RETAINED', dataRetentionUntil: retentionDate!, automaticDeletion: false as const } } : periodAdjusted
  useEffect(() => {
    const deadlines = [end, Date.parse(retentionDate ?? '')].filter(value => Number.isFinite(value) && value > Date.now())
    if (!deadlines.length) return
    const timer = window.setTimeout(() => { setNow(Date.now()); void query.refetch() }, Math.min(2147483647, Math.max(1, Math.min(...deadlines) - Date.now())))
    return () => window.clearTimeout(timer)
  }, [end, retentionDate, query.refetch, now])
  const access = subscription?.access ?? query.data?.access
  return { ...query, missingTenant, subscription, canRead: !query.isError && access?.canRead === true && !retentionOver,
    canWrite: !query.isError && access?.canWrite === true && !expired }
}
